import type { UserPermissions } from '../usersApi/users/dto/user-permissions.dto'

// Permissões da interface — valem só na tela, o backend não as confere.
// Elas vêm no token (window.store.token.permissions), já completadas pelo users-api com os padrões do molde
// (UserPermissions), e são relidas a cada renovação do token.

// todos os caminhos "a.b.c" que terminam em boolean (ex.: 'pos.sales.write')
type BooleanPaths<T> = {
    [K in keyof T & string]: T[K] extends boolean ? K : T[K] extends object ? `${K}.${BooleanPaths<T[K]>}` : never
}[keyof T & string]

export type PermissionPath = BooleanPaths<UserPermissions>

function read(path: string): unknown {
    let node: any = window.store?.token?.permissions
    for (const key of path.split('.')) {
        if (node === null || node === undefined) return undefined
        node = node[key]
    }
    return node
}

/**
 * Diz se o usuário logado tem a permissão. Super usuário pode tudo.
 * `fallback` vale quando a chave não existe no token: o padrão é negar, mas uma chave recém-criada
 * (que tokens antigos ou um users-api ainda não publicado não trazem) deve passar `true`,
 * igual ao padrão do molde no backend — senão o recurso some para todo mundo até o token renovar.
 */
export function can(path: PermissionPath, fallback: boolean = false): boolean {
    if (window.store?.token?.permissions?.super) return true
    const value = read(path)
    if (typeof value !== 'boolean') return fallback
    return value
}

/** Abas do Financeiro, na ordem da tela. A primeira liberada é a "home" de quem não pode ver a de início. */
export const financialTabs: { title: string; url: string; path: PermissionPath }[] = [
    { title: 'Financeiro', url: '/financial-4/home', path: 'financial.tabs.home' },
    { title: 'A receber', url: '/financial-4/bills-to-receive', path: 'financial.tabs.billsToReceive' },
    { title: 'A pagar', url: '/financial-4/bills-to-pay', path: 'financial.tabs.billsToPay' },
    { title: 'PDV', url: '/financial-4/pos', path: 'financial.tabs.pos' },
    { title: 'Movimentação', url: '/financial-4/accounts', path: 'financial.tabs.accounts' },
    { title: 'Relatórios', url: '/financial-4/reports', path: 'financial.tabs.reports' },
]

export function canFinancialTab(path: PermissionPath): boolean {
    // abas granuladas em 2026-10: tokens anteriores não têm financial.tabs, e aí vale o comportamento antigo (tudo liberado)
    return can('financial.show') && can(path, true)
}

// Categorias do catálogo de relatórios (v2, vindas do zandu-api) → permissão. O catálogo só manda o nome
// da categoria; se ele mudar lá, a categoria cai no caso "sem permissão própria" de canReportCategory.
const reportCategories: Record<string, PermissionPath> = {
    Financeiro: 'reports.financials',
    Pessoas: 'reports.persons',
    Vendas: 'reports.pos',
    'Agenda e atendimento': 'reports.schedules',
    NPS: 'reports.nps',
    Estoque: 'reports.stock',
    'Assinaturas no ZanduPay': 'reports.pay',
}

export function canReportCategory(category: string): boolean {
    if (!can('reports.show')) return false
    const path = reportCategories[category]
    // categoria nova no catálogo, ainda sem permissão própria: segue o acesso aos relatórios
    if (!path) return true
    // nps/stock/pay criadas em 2026-10: tokens anteriores não as têm, e aí fica como antes (liberado)
    return can(path, true)
}

/** URL da primeira aba do Financeiro liberada, ou null se nenhuma estiver. */
export function financialHome(): string | null {
    return financialTabs.find((tab) => canFinancialTab(tab.path))?.url ?? null
}
